/**
 * SUMMARY.md parser — extracts GitBook-style navigation structure.
 *
 * Supports:
 *   # Title
 *   * [Page Title](path.md)
 *   * [Group Name]
 *     * [Child Page](child.md)
 */

export interface SummaryEntry {
  title: string;
  path?: string; // undefined for group headers
  children: SummaryEntry[];
}

export interface ParsedSummary {
  title?: string;
  entries: SummaryEntry[];
}

export function parseSummary(content: string): ParsedSummary {
  const lines = content.split('\n');
  let title: string | undefined;
  const rootEntries: SummaryEntry[] = [];
  const stack: { entries: SummaryEntry[]; indent: number }[] = [{ entries: rootEntries, indent: -1 }];

  for (const line of lines) {
    const trimmed = line.trim();

    // Extract title from first H1
    if (!title && trimmed.startsWith('# ')) {
      title = trimmed.slice(2).trim();
      continue;
    }

    // Section headers (## Header)
    const headerMatch = trimmed.match(/^##\s+(.+)$/);
    if (headerMatch) {
      const entry: SummaryEntry = { title: headerMatch[1], children: [] };
      rootEntries.push(entry);
      // Reset stack to this group level
      stack.length = 1;
      stack.push({ entries: entry.children, indent: 0 });
      continue;
    }

    // List items: * [Title](path.md) or - [Title](path.md)
    const listMatch = line.match(/^(\s*)[*\-+]\s+(.+)$/);
    if (!listMatch) continue;

    const indent = listMatch[1].length;
    const itemText = listMatch[2];

    // Parse link: [Title](path.md) or just [Title] for groups
    const linkMatch = itemText.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    const groupMatch = itemText.match(/^\[([^\]]+)\]$/);

    let entry: SummaryEntry;
    if (linkMatch) {
      entry = { title: linkMatch[1], path: linkMatch[2], children: [] };
    } else if (groupMatch) {
      entry = { title: groupMatch[1], children: [] };
    } else {
      // Plain text item
      entry = { title: itemText, children: [] };
    }

    // Find correct parent based on indent
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    const parent = stack[stack.length - 1];
    parent.entries.push(entry);

    // Push this entry's children for potential nesting
    stack.push({ entries: entry.children, indent });
  }

  return { title, entries: rootEntries };
}

/**
 * Flatten a parsed summary into a linear page order (for prev/next navigation).
 */
export function flattenSummary(entries: SummaryEntry[]): { title: string; path: string }[] {
  const pages: { title: string; path: string }[] = [];

  function walk(items: SummaryEntry[]) {
    for (const entry of items) {
      if (entry.path) {
        pages.push({ title: entry.title, path: entry.path });
      }
      if (entry.children.length > 0) {
        walk(entry.children);
      }
    }
  }

  walk(entries);
  return pages;
}
